import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { deleteSessionItem, getSessionItem, setSessionItem } from '@atlassian/search-dialog';

import { PROMPT_OVERRIDE_SESSION_KEY } from '../../constants';

import { messages } from './messages';

const ColoredEditIcon = () => <EditIcon color={token('color.icon.subtlest')} label="" />;

export const PromptOverrideButton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isPromptOverrideModalOpen, setIsPromptOverrideModalOpen] = useState(false);
	const [promptText, setPromptText] = useState('');

	// Load stored prompt when modal opens
	useEffect(() => {
		if (isPromptOverrideModalOpen) {
			const stored = getSessionItem(PROMPT_OVERRIDE_SESSION_KEY);
			setPromptText(stored || '');
		}
	}, [isPromptOverrideModalOpen]);

	const openPromptOverrideModal = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setIsPromptOverrideModalOpen(true);
	}, []);

	const closePromptOverrideModal = useCallback(() => {
		setIsPromptOverrideModalOpen(false);
	}, []);

	const handleSave = useCallback(() => {
		const trimmedPrompt = promptText.trim();

		if (trimmedPrompt) {
			setSessionItem(PROMPT_OVERRIDE_SESSION_KEY, trimmedPrompt);
		} else {
			deleteSessionItem(PROMPT_OVERRIDE_SESSION_KEY);
		}

		closePromptOverrideModal();
	}, [promptText, closePromptOverrideModal]);

	const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setPromptText(e.target.value);
	}, []);

	return (
		<Fragment>
			<IconButton
				icon={ColoredEditIcon}
				label={formatMessage(messages.prompt_override_button_label)}
				onClick={openPromptOverrideModal}
				appearance="subtle"
				isTooltipDisabled={false}
				spacing="compact"
				testId="prompt-override-button"
				color={token('color.icon.subtlest')}
				shape={fg('kd_sain_feedback_collector_ux_uplift_rollout') ? 'circle' : 'default'}
			/>

			<ModalTransition>
				{isPromptOverrideModalOpen && (
					<Modal onClose={closePromptOverrideModal} width="large" testId="prompt-override-modal">
						<ModalHeader hasCloseButton>
							<ModalTitle>
								<FormattedMessage {...messages.prompt_override_modal_title} />
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Stack space="space.200">
								<Box>
									<Stack space="space.100">
										<Text weight="semibold">
											<FormattedMessage {...messages.prompt_override_custom_prompt_label} />
										</Text>
										<TextArea
											value={promptText}
											onChange={handleTextChange}
											placeholder={formatMessage(messages.prompt_override_textarea_placeholder)}
											testId="prompt-override-textarea"
											resize="vertical"
										/>
										<Inline space="space.100" spread="space-between">
											<Text size="small" color="color.text.subtlest">
												<FormattedMessage {...messages.prompt_override_help_text} />
											</Text>
										</Inline>
									</Stack>
								</Box>
							</Stack>
						</ModalBody>
						<ModalFooter>
							<Inline space="space.100">
								<Button appearance="subtle" onClick={closePromptOverrideModal}>
									<FormattedMessage {...messages.prompt_override_cancel_button} />
								</Button>
								<Button
									appearance="primary"
									onClick={handleSave}
									testId="prompt-override-save-button"
								>
									<FormattedMessage {...messages.prompt_override_save_button} />
								</Button>
							</Inline>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</Fragment>
	);
};
